/* START_MODULE_CONTRACT
 * PURPOSE: React error boundary — ловит ошибки рендера дочерних компонентов и показывает fallback вместо белого экрана.
 * SCOPE: componentDidCatch, fallback UI, сброс состояния ошибки.
 * DEPENDS: нет
 * LINKS: M-FE-APP
 * ROLE: RUNTIME
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * ErrorBoundary - класс-обёртка, перехватывающая ошибки рендера поддерева.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-09 - Создан для защиты таймлайна от белого экрана при ошибке рендера.
 * END_CHANGE_SUMMARY
 */

import { Component } from 'react';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh';

/* START_CLASS_ErrorBoundary */
/* START_CONTRACT: ErrorBoundary
 * PURPOSE: Перехватывать ошибки рендера дочернего дерева и отображать восстановимый fallback.
 * INPUTS: { children: ReactNode, fallbackMessage?: string, onReset?: ()=>void }.
 * OUTPUTS: { JSX.Element - children либо fallback }.
 * SIDE_EFFECTS: локальный state hasError; console.error при перехвате.
 * LINKS: M-FE-APP
 * END_CONTRACT: ErrorBoundary
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
    this.handleReset = this.handleReset.bind(this);
  }

  /* START_METHOD_getDerivedStateFromError */
  /* START_CONTRACT: getDerivedStateFromError
   * PURPOSE: Перевести boundary в состояние ошибки при исключении рендера.
   * INPUTS: { error: Error }.
   * OUTPUTS: { { hasError: true } }.
   * SIDE_EFFECTS: нет.
   * LINKS: M-FE-APP
   * END_CONTRACT: getDerivedStateFromError
   */
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  /* END_METHOD_getDerivedStateFromError */

  /* START_METHOD_componentDidCatch */
  /* START_CONTRACT: componentDidCatch
   * PURPOSE: Залогировать перехваченную ошибку рендера для диагностики.
   * INPUTS: { error: Error, info: { componentStack: string } }.
   * OUTPUTS: { void }.
   * SIDE_EFFECTS: console.error с маркером.
   * LINKS: M-FE-APP
   * END_CONTRACT: componentDidCatch
   */
  componentDidCatch(error, info) {
    /* START_BLOCK_CAPTURE_ERROR */
    console.error('[ErrorBoundary][componentDidCatch][RENDER_ERROR] child render failed', {
      message: error?.message,
      componentStack: info?.componentStack,
    });
    /* END_BLOCK_CAPTURE_ERROR */
  }
  /* END_METHOD_componentDidCatch */

  /* START_METHOD_handleReset */
  /* START_CONTRACT: handleReset
   * PURPOSE: Сбросить состояние ошибки и уведомить родителя (для повторной попытки).
   * INPUTS: {}.
   * OUTPUTS: { void }.
   * SIDE_EFFECTS: setState; вызов props.onReset при наличии.
   * LINKS: M-FE-APP
   * END_CONTRACT: handleReset
   */
  handleReset() {
    this.setState({ hasError: false });
    if (this.props.onReset) {
      this.props.onReset();
    }
  }
  /* END_METHOD_handleReset */

  /* START_METHOD_render */
  /* START_CONTRACT: render
   * PURPOSE: Вернуть children или восстановимый fallback при ошибке.
   * INPUTS: {}.
   * OUTPUTS: { JSX.Element }.
   * SIDE_EFFECTS: нет.
   * LINKS: M-FE-APP
   * END_CONTRACT: render
   */
  render() {
    if (this.state.hasError) {
      /* START_BLOCK_FALLBACK */
      const { fallbackMessage } = this.props;
      return (
        <Box sx={{ py: 2 }}>
          <Alert
            severity="error"
            action={(
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={this.handleReset}
              >
                Повторить
              </Button>
            )}
          >
            {fallbackMessage || 'Не удалось отобразить раздел. Попробуйте ещё раз.'}
          </Alert>
        </Box>
      );
      /* END_BLOCK_FALLBACK */
    }
    return this.props.children;
  }
  /* END_METHOD_render */
}
/* END_CLASS_ErrorBoundary */
